import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import type { DevEnv } from "@p0security/shared/types/environment";

export type DbEngine = "mysql" | "pg";

export type InstallTfParams = {
  id: string;
  type: "cluster" | "db";
  hashId: (id: string) => string;
  awsRegion: string;
} & (
  | { connectorArn: string; connectorId?: never } // installing a new connector
  | { connectorId: string; connectorArn?: never } // reusing an existing connector
);

/** Inputs available to the per-engine install module's body */
export type DbInstallModuleContext = {
  /** Terraform data source name: `aws_db_instance` or `aws_rds_cluster` */
  dbDataSource: string;
  /** Stable hashed identifier used to namespace generated TF symbols */
  tfId: string;
  /** Whether the target RDS object is a standalone instance or a cluster */
  type: "cluster" | "db";
  /** TF reference to the RDS object's ARN attribute */
  rdsArnRef: string;
};

export type DbInstallModuleSpec = {
  /** Pinned version of the `p0-security/p0-db/aws` module */
  version: string;
  /** Engine-specific module arguments — emitted before the shared
   * `connector_security_group_id` / `lambda_execution_role_name` lines.
   */
  arguments: (context: DbInstallModuleContext) => string;
};

/**
 * Builds the per-engine `installTf` function shown to users during DB
 * `iam-write` setup.
 *
 * The connector lambda is one-per-VPC-per-engine: the first DB onboarded
 * in a VPC installs it via the connector module; subsequent DBs reuse
 * it through a lookup data source. That branching and the surrounding
 * data-source / module wiring are identical across engines and live
 * here; only the install-module version and arguments vary.
 */
export const dbInstallTfFactory =
  (engine: DbEngine, installModule: DbInstallModuleSpec) =>
  (context: DevEnv, params: InstallTfParams) => {
    const iamRole = awsRoleName(context, "iam-write");
    const dbTag = params.type === "cluster" ? "cluster" : "db_instance";
    const dbDataSource =
      params.type === "cluster" ? "aws_rds_cluster" : "aws_db_instance";
    // aws_rds_cluster exports `db_subnet_group_name`, but aws_db_instance
    // exports `db_subnet_group` (still the name, despite the shorter attr).
    const subnetGroupAttr =
      params.type === "cluster" ? "db_subnet_group_name" : "db_subnet_group";

    // On the reuse path we look the lambda up via a data source and derive the
    // SG and execution role from it, since the connector module isn't (and
    // shouldn't be) re-declared here.
    const isConnectorInstalled = !!params.connectorId;
    const tfId = params.hashId(params.id);

    // The default `aws` provider may target a different region than the RDS
    // database. Configure an aliased provider pinned to the database's region
    // and route every data source / module through it.
    const providerAlias = `database_${tfId}`;
    const providerRef = `aws.${providerAlias}`;
    const rdsArnAttr = params.type === "cluster" ? "arn" : "db_instance_arn";
    const rdsArnRef = `data.${dbDataSource}.database_${tfId}.${rdsArnAttr}`;

    const connectorSecurityGroupExpr = isConnectorInstalled
      ? `one(data.aws_lambda_function.connector_${tfId}.vpc_config[0].security_group_ids)`
      : `module.aws_db_connector_install_${tfId}.connector_security_group.id`;
    const lambdaRoleSource = isConnectorInstalled
      ? `data.aws_lambda_function.connector_${tfId}.role`
      : `module.aws_db_connector_install_${tfId}.lambda.role`;
    const lambdaExecutionRoleNameExpr = `reverse(split("/", reverse(split(":", ${lambdaRoleSource}))[0]))[0]`;

    const installModuleArguments = installModule.arguments({
      dbDataSource,
      tfId,
      type: params.type,
      rdsArnRef,
    });

    return [
      `
provider "aws" {
  alias  = "${providerAlias}"
  region = "${params.awsRegion}"
}

data "${dbDataSource}" "database_${tfId}" {
  provider = ${providerRef}

  ${dbTag}_identifier = "${params.id}"
}
`.trim(),
      ...[
        params.connectorId
          ? `
data "aws_lambda_function" "connector_${tfId}" {
  provider = ${providerRef}

  function_name = "${params.connectorId}"
}
`.trim()
          : `
data "aws_db_subnet_group" "database_${tfId}" {
  provider = ${providerRef}

  name = data.${dbDataSource}.database_${tfId}.${subnetGroupAttr}
}

module "aws_db_connector_install_${tfId}" {
  source  = "p0-security/p0-connector/aws"
  version = "0.6.0"

  providers = { aws = ${providerRef} }

  aws_role_name = "${iamRole}"
  aws_services  = ["rds"]
  setup_vpc_endpoints = true

  service            = "${engine}"
  service_subnet_ids = data.aws_db_subnet_group.database_${tfId}.subnet_ids
  vpc_id             = data.aws_db_subnet_group.database_${tfId}.vpc_id

  connector_arn = "${params.connectorArn}"
}
        `.trim(),
      ],
      `
module "aws_${engine}_install_${tfId}" {
  source  = "p0-security/p0-db/aws"
  version = "${installModule.version}"

  providers = { aws = ${providerRef} }

  ${installModuleArguments}

  connector_security_group_id = ${connectorSecurityGroupExpr}
  lambda_execution_role_name  = ${lambdaExecutionRoleNameExpr}
}
`.trim(),
    ]
      .join("\n\n")
      .trim();
  };
